import React, { useEffect, useMemo, useRef, useState } from 'react';
import '../App.css';
import { Storage, API } from 'aws-amplify';

const S3_KEY = 'nfl/daily.json';
const S3_LEVEL = 'public';
const FALLBACK_SEASON_START_ISO = '2026-09-10T00:00:00Z';

// --- WEBSOCKET: set your deployed wss endpoint -----------------------------
const WS_URL = 'wss://6hdt5jitba.execute-api.us-east-1.amazonaws.com/production';
// ---------------------------------------------------------------------------

// --- helpers ---
const sanitizeTeam = (name) => (name || '').replace(/\s+/g, '');
const playerOptions = [
  { label: 'Mark', value: 'Mark', id: 1 },
  { label: 'Bill', value: 'Bill', id: 2 },
  { label: 'None', value: 'None', id: 0 },
];

const userName = (id) => (Number(id) === 1 ? 'Mark' : Number(id) === 2 ? 'Bill' : 'Unknown');

// Normalize a "pair" signature independent of home/away (fallback if no game_id saved)
const pairSig = (homeNoSpaces, awayNoSpaces) => {
  const a = (homeNoSpaces || '').toLowerCase();
  const b = (awayNoSpaces || '').toLowerCase();
  return a < b ? `${a}|${b}` : `${b}|${a}`;
};

const gameIdFromRow = (g) =>
  `GAME#${g._week ?? 0}#${g.kickoff_iso || 'NA'}#${sanitizeTeam(g.team_home)}#${sanitizeTeam(g.team_away)}`;

// Robustly parse spread strings like "PK", "Pick", "Even", "+5.5", " - 2.5 ", etc.
const normalizeSpread = (val) => {
  if (val == null) return 0;
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  const s = String(val).trim().toUpperCase();
  if (s === 'PK' || s === 'PICK' || s === 'EVEN' || s === 'EV') return 0;
  const num = Number(s.replace(/[^\d.+-]/g, '')); // keep digits, +, -, .
  return Number.isFinite(num) ? num : 0;
};

// Format any number into a clean JSON string for the backend (handles decimals safely)
const numStr = (v, opts = {}) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '0';
  const n = Number(v);
  if (opts.fixed != null) return n.toFixed(opts.fixed);
  return String(n);
};

// Decide wager by week number. Adjust if your JSON has explicit postseason flags.
const computeWager = (weekNum) => {
  if (weekNum == null) return 20;  // default
  if (weekNum <= 18) return 20;    // regular season
  if (weekNum === 23) return 100;  // typical Super Bowl slot if contiguous weeks
  return 40;                       // other postseason rounds
};

// Unique key for a game row (stable across renders)
const gameKey = (g) => [
  g.kickoff_iso || 'no_kick',
  g.team_home || 'no_home',
  g.team_away || 'no_away',
].join('|');

function Wager() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState('all'); // 'all' | number (stringified)
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  // UI state: player pick & team selection per game, submitted rows, and "picked by"
  const [playerByGame, setPlayerByGame] = useState({});     // { [gameKey]: 'Mark'|'Bill'|'None' }
  const [pickByGame, setPickByGame] = useState({});         // { [gameKey]: 'home'|'away' }
  const [submitted, setSubmitted] = useState({});           // { [gameKey]: true }
  const [pickedBy, setPickedBy] = useState({});             // { [gameKey]: 'Mark'|'Bill' }
  const [submitting, setSubmitting] = useState({});         // { [gameKey]: true } (in-flight)

  const wsStartedRef = useRef(false);   // prevents StrictMode double-connect
  const wsRef = useRef(null);           // keep the live socket instance

  // -------- load from S3 ----------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await Storage.get(S3_KEY, { level: S3_LEVEL, download: true });
        const blob = res?.Body instanceof Blob ? res.Body : res;
        const text = await blob.text();
        const parsed = JSON.parse(text);
        if (!cancelled) setData(parsed);
      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // --------------------------------

  const seasonStartISO = useMemo(
    () => data?.season_start || FALLBACK_SEASON_START_ISO,
    [data]
  );

  // Derive week if missing, else use provided (inlined to avoid hook dep warning)
  const gamesWithWeek = useMemo(() => {
    if (!data?.games) return [];
    return data.games.map(g => {
      let w;
      if (g.week != null) {
        w = Number(g.week);
      } else if (g.kickoff_iso) {
        const kickoff = new Date(g.kickoff_iso);
        const start = new Date(seasonStartISO);
        const diffDays = Math.floor((kickoff - start) / 86400000); // ms/day
        w = diffDays >= 0 ? Math.floor(diffDays / 7) + 1 : undefined;
      }
      return { ...g, _week: w };
    });
  }, [data, seasonStartISO]);

  const availableWeeks = useMemo(() => {
    const s = new Set();
    gamesWithWeek.forEach(g => { if (g._week != null) s.add(g._week); });
    return Array.from(s).sort((a, b) => a - b);
  }, [gamesWithWeek]);

  // ---------- auto-select the week containing the next game ----------
  useEffect(() => {
    if (week !== 'all' || availableWeeks.length === 0) return;

    const now = Date.now();
    const nextGame = gamesWithWeek
      .filter(g => g.kickoff_iso && new Date(g.kickoff_iso).getTime() > now)
      .sort((a, b) => new Date(a.kickoff_iso) - new Date(b.kickoff_iso))[0];
    const chosen = nextGame?._week ?? availableWeeks[availableWeeks.length - 1];

    setWeek(String(chosen));
  }, [availableWeeks, gamesWithWeek, week]);
  // ----------------------------------------------------------------------

  const filtered = useMemo(() => {
    if (week === 'all') return gamesWithWeek;
    const target = Number(week);
    return gamesWithWeek.filter(g => g._week === target);
  }, [gamesWithWeek, week]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  // Preload existing picks from the backend, grey out, and store "picked by"
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const res = await API.post('buckluckyapi1', '/picks3', {
          headers: { 'Content-Type': 'application/json' },
          body: { Action: 'getBets', year: 2026 }
        });

        const items = Array.isArray(res) ? res : (res?.Items || res?.items || []);

        // Build lookups
        const byGameId = new Map(); // game_id -> item
        const byWeekAndPair = new Map(); // legacy picks without game_id

        for (const it of items) {
          const gid = it.game_id ? String(it.game_id) : '';
          if (gid) {
            byGameId.set(gid, it);
            continue;
          }

          const pt = (it.picked_team_name || '').replace(/\s+/g, '');
          const ot = (it.opposing_team_name || '').replace(/\s+/g, '');
          // Older picks only have teams and season_week; never match across weeks.
          if (pt && ot && it.season_week != null) {
            byWeekAndPair.set(`${Number(it.season_week)}|${pairSig(pt, ot)}`, it);
          }
        }

        const nextSubmitted = {};
        const nextPickedBy = {};

        for (const g of filtered) {
          const key = `${g.kickoff_iso || 'no_kick'}|${g.team_home || 'no_home'}|${g.team_away || 'no_away'}`;
          const gid = `GAME#${g._week ?? 0}#${g.kickoff_iso || 'NA'}#${(g.team_home||'').replace(/\s+/g,'')}#${(g.team_away||'').replace(/\s+/g,'')}`;

          let matched = byGameId.get(gid);
          if (!matched) {
            const homeNoSp = (g.team_home || '').replace(/\s+/g, '');
            const awayNoSp = (g.team_away || '').replace(/\s+/g, '');
            matched = byWeekAndPair.get(`${g._week}|${pairSig(homeNoSp, awayNoSp)}`);
          }

          if (matched) {
            nextSubmitted[key] = true;
            nextPickedBy[key] = userName(matched.pick_user_id);
          }
        }

        if (!cancelled) {
          if (Object.keys(nextSubmitted).length) setSubmitted(prev => ({ ...prev, ...nextSubmitted }));
          if (Object.keys(nextPickedBy).length) setPickedBy(prev => ({ ...prev, ...nextPickedBy }));
        }
      } catch (e) {
        console.error('Preload picks failed:', e);
      }
    })();

    return () => { cancelled = true; };
  }, [filtered]);

  // --- WEBSOCKET: connect once & live updates (Strict-Mode safe + diagnostics) ----------
  const gamesRef = useRef([]);
  useEffect(() => { gamesRef.current = gamesWithWeek; }, [gamesWithWeek]);

  useEffect(() => {
    if (!WS_URL || WS_URL.startsWith('wss://<')) return;

    // Prevent React 18 StrictMode double-connect
    if (wsStartedRef.current) return;
    wsStartedRef.current = true;

    let retry = 0;
    const maxDelay = 30000;
    const backoff = () => Math.min(maxDelay, 1000 * 2 ** retry++);
    let shouldReconnect = true;
    let pingTimer = null;

    const startPing = (ws) => {
      stopPing();
      // keep-alive every 4 minutes (API GW idle ~10m)
      pingTimer = setInterval(() => {
        try { if (ws?.readyState === 1) ws.send(JSON.stringify({ action: 'ping', t: Date.now() })); } catch {}
      }, 240000);
    };
    const stopPing = () => { if (pingTimer) { clearInterval(pingTimer); pingTimer = null; } };

    const connect = () => {
      if (!shouldReconnect) return;

      const url = `${WS_URL}?client=web&v=1`;
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retry = 0;
        try { ws.send(JSON.stringify({ action: 'hello', ts: Date.now() })); } catch {}
        startPing(ws);
      };

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg?.type !== 'pick.created') return;

          const p = msg.payload || {};
          const gid = String(p.game_id || '');
          if (!gid) return;

          const match = (gamesRef.current || []).find(g =>
            (`GAME#${g._week ?? 0}#${g.kickoff_iso || 'NA'}#${(g.team_home||'').replace(/\s+/g,'')}#${(g.team_away||'').replace(/\s+/g,'')}`) === gid
          );
          if (!match) return;

          const key = `${match.kickoff_iso || 'no_kick'}|${match.team_home || 'no_home'}|${match.team_away || 'no_away'}`;
          setSubmitted(prev => ({ ...prev, [key]: true }));
          setPickedBy(prev => ({ ...prev, [key]: userName(p.pick_user_id) })); // NEW: record who picked
        } catch (err) {
          console.error('[WS] parse error', err);
        }
      };

      ws.onclose = () => {
        stopPing();
        if (!shouldReconnect) return;
        setTimeout(connect, backoff());
      };

      ws.onerror = () => {
        // Let onclose handle reconnection
      };
    };

    connect();

    return () => {
      shouldReconnect = false;
      stopPing();
      const ws = wsRef.current;
      try { if (ws && ws.readyState === 1) ws.close(1000, 'component unmount'); } catch {}
    };
  }, []); // connect once
  // --------------------------------------------------------------------------

  // --- submit handler ---
  const handleSubmit = async (g) => {
    const key = gameKey(g);
    const selectedPlayer = playerByGame[key] || 'None';
    const pickedSide = pickByGame[key]; // 'home' | 'away'

    if (submitted[key] || submitting[key]) return;

    // Basic validations
    if (selectedPlayer === 'None') {
      alert('Please choose a Player (Mark or Bill).');
      return;
    }
    if (!pickedSide) {
      alert('Please select either the Home Team or the Away Team.');
      return;
    }

    // Build payload pieces
    const home = g.team_home || '';
    const away = g.team_away || '';
    const pickIsHome = pickedSide === 'home';
    const pickedTeamNameRaw = pickIsHome ? home : away;
    const opposingTeamNameRaw = pickIsHome ? away : home;

    const picked_team_name = sanitizeTeam(pickedTeamNameRaw);     // remove spaces
    const opposing_team_name = sanitizeTeam(opposingTeamNameRaw); // remove spaces

    // Normalize home line; away line is inverse
    const spreadHome = normalizeSpread(g.spread_home);
    const linePickedNum = pickIsHome ? spreadHome : (spreadHome * -1);

    // player → id
    const playerMap = { Mark: 1, Bill: 2, None: 0 };
    const pick_user_id_num = playerMap[selectedPlayer] ?? 0;

    const season_week_num = g._week ?? 0;
    const wager_num = computeWager(season_week_num);

    // derive IDs for GSIs / queries
    const game_id = gameIdFromRow(g);
    const user_id = `USER#${pick_user_id_num}`;

    // Send numeric fields as strings to avoid decimal parsing issues on backend
    const body = {
      Action: 'createWager',
      line_picked_team: numStr(
        linePickedNum,
        { fixed: Number.isInteger(linePickedNum) ? undefined : 1 } // keep ".5" as "x.x"
      ),
      opposing_team_name,
      pick_user_id: numStr(pick_user_id_num),
      picked_team_name,
      pk: numStr(2026),
      score_opposing_team: numStr(0),
      score_picked_team: numStr(0),
      season_week: numStr(season_week_num),
      wager: numStr(wager_num),
      // extra fields (queried/broadcast)
      game_id,
      user_id,
      createdAt: new Date().toISOString()
    };

    try {
      setSubmitting(prev => ({ ...prev, [key]: true }));
      await API.post('buckluckyapi1', '/picks3', {
        headers: { 'Content-Type': 'application/json' },
        body
      });
      // Grey out & show who picked immediately (no need to wait for WS echo)
      setSubmitted(prev => ({ ...prev, [key]: true }));
      setPickedBy(prev => ({ ...prev, [key]: selectedPlayer })); // NEW
    } catch (e) {
      const httpStatus = e?.response?.status;
      const data = e?.response?.data;
      const hdrs = e?.response?.headers || {};
      const reqId = hdrs['x-amzn-requestid'] || hdrs['x-amz-apigw-id'] || '(none)';
      console.error('Submit failed', { httpStatus, data, headers: hdrs, error: e });

      if (httpStatus === 409) {
        alert('That game has already been picked.');
        setSubmitted(prev => ({ ...prev, [key]: true }));
        return;
      }

      alert(
        `Submit failed (HTTP ${httpStatus ?? 'n/a'}).\n` +
        `APIGW Request-Id: ${reqId}\n` +
        `Body: ${typeof data === 'string' ? data : JSON.stringify(data)}`
      );
    } finally {
      setSubmitting(prev => ({ ...prev, [key]: false }));
    }
  };

  // --- render ---
  return (
    <div className="content">
      {loading && <p>Loading JSON from S3…</p>}
      {error && <p style={{ color: 'crimson' }}>Error: {error}</p>}

      {data && (
        <>
          <h2 style={{ marginBottom: 8 }}>
            Generated at: {data.generated_at ? formatDate(data.generated_at) : '—'}
          </h2>

          <div style={{ display: 'flex', gap: 12, alignItems: 'center', margin: '8px 0 16px' }}>
            <label>
              <span style={{ marginRight: 8, fontWeight: 600 }}>Week:</span>
              <select value={week} onChange={e => setWeek(e.target.value)}>
                <option value="all">All</option>
                {availableWeeks.map(w => (
                  <option key={w} value={String(w)}>Week {w}</option>
                ))}
              </select>
            </label>
            <small style={{ opacity: 0.7 }}>
              Lines supplied by DraftKings
            </small>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 960, width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f7f7f7' }}>
                  <th style={th}>Week</th>
                  <th style={th}>Kickoff</th>
                  <th style={th}>Player</th>
                  <th style={th}>Home Team</th>
                  <th style={th}>Spread</th>
                  <th style={th}>Away Team</th>
                  {/* NEW: Picked By column */}
                  <th style={{ ...th, textAlign: 'center' }}>Picked By</th>
                  <th style={{ ...th, textAlign: 'center' }}>Submit</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((g, i) => {
                  const key = gameKey(g);
                  const isSubmitted = !!submitted[key];
                  const isSubmitting = !!submitting[key];
                  const rowStyle = {
                    ...((i % 2) ? rowAlt : row),
                    ...(isSubmitted ? { opacity: 0.55 } : {}),
                  };

                  return (
                    <tr key={key} style={rowStyle}>
                      <td style={tdCenter}>{g._week ?? '—'}</td>
                      <td style={td}>{g.kickoff_iso ? formatDate(g.kickoff_iso) : '—'}</td>

                      {/* Player dropdown */}
                      <td style={{ ...td, minWidth: 140 }}>
                        <select
                          value={playerByGame[key] ?? 'None'}
                          disabled={isSubmitted}
                          onChange={(e) =>
                            setPlayerByGame(prev => ({ ...prev, [key]: e.target.value }))
                          }
                        >
                          {playerOptions.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* Home Team (selectable) */}
                      <td
                        style={{
                          ...td,
                          fontWeight: 600,
                          color: '#09168aff',
                          cursor: isSubmitted ? 'default' : 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={() => !isSubmitted && setPickByGame(prev => ({ ...prev, [key]: 'home' }))}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="radio"
                            name={`pick-${key}`}
                            checked={(pickByGame[key] || '') === 'home'}
                            disabled={isSubmitted}
                            onChange={() => setPickByGame(prev => ({ ...prev, [key]: 'home' }))}
                          />
                          <span>{g.team_home || '—'}</span>
                        </label>
                      </td>

                      {/* Spread (home line from JSON) */}
                      <td style={{ ...tdCenter, fontVariantNumeric: 'tabular-nums', minWidth: 80 }}>
                        {g.spread_home ?? '—'}
                      </td>

                      {/* Away Team (selectable) */}
                      <td
                        style={{
                          ...td,
                          fontWeight: 600,
                          color: '#09168aff',
                          cursor: isSubmitted ? 'default' : 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={() => !isSubmitted && setPickByGame(prev => ({ ...prev, [key]: 'away' }))}
                      >
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="radio"
                            name={`pick-${key}`}
                            checked={(pickByGame[key] || '') === 'away'}
                            disabled={isSubmitted}
                            onChange={() => setPickByGame(prev => ({ ...prev, [key]: 'away' }))}
                          />
                          <span>{g.team_away || '—'}</span>
                        </label>
                      </td>

                      {/* NEW: Picked By cell */}
                      <td style={{ ...tdCenter, minWidth: 100, opacity: isSubmitted ? 0.9 : 0.6 }}>
                        {pickedBy[key] || '—'}
                      </td>

                      {/* Submit button */}
                      <td style={{ ...tdCenter, minWidth: 120 }}>
                        <button
                          onClick={() => handleSubmit(g)}
                          disabled={
                            isSubmitted ||
                            isSubmitting ||
                            (playerByGame[key] ?? 'None') === 'None' ||
                            !(pickByGame[key])
                          }
                          style={{
                            padding: '6px 10px',
                            borderRadius: 6,
                            border: '1px solid #ccc',
                            background: isSubmitted ? '#ddd'
                                    : isSubmitting ? '#eee'
                                    : '#f5f9ff',
                            cursor: (isSubmitted || isSubmitting) ? 'not-allowed' : 'pointer',
                          }}
                          title={
                            isSubmitted ? 'Submitted'
                            : isSubmitting ? 'Submitting...'
                            : 'Submit pick'
                          }
                        >
                          {isSubmitted ? 'Submitted' : (isSubmitting ? 'Submitting…' : 'Submit')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {!filtered.length && (
                  <tr><td style={{ ...td, padding: 16 }} colSpan={8}>No games for this selection.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

// table styles
const th = { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #ddd' };
const td = { padding: '8px', borderBottom: '1px solid #eee' };
const tdCenter = { ...td, textAlign: 'center' };
const row = {};
const rowAlt = { background: '#fafafa' };

export default Wager;

